import { moduleRegistry } from "@/components/modules/registry";
import { seededEvents } from "@/data/seed-imam-ali-2026";
import { api } from "@/lib/api-client";
import { cn } from "@/lib/cn";
import type { Event, EventTheme, ModuleId } from "@/lib/event-schema";
import { type ExtractedTheme, processBanner, uploadBanner } from "@/lib/image-pipeline";
import { NotFoundView } from "@/routes/NotFound";
import {
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronUp,
  GripVertical,
  ImagePlus,
  Loader2,
  Moon,
  Power,
  Save,
  Sun,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; event: Event }
  | { status: "not-found" };

type SaveState = "idle" | "saving" | "saved" | "error";

export function AdminEventEditor() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [save, setSave] = useState<SaveState>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [dirty, setDirty] = useState(false);
  // Module currently being dragged (drag handle pressed) and the row it
  // is hovering over. Both clear when the drag ends or the drop lands.
  // Kept at the editor scope, not per row, so any row's drop handler can
  // read dragId and any onDragOver can update dropTargetId.
  const [dragId, setDragId] = useState<ModuleId | null>(null);
  const [dropTargetId, setDropTargetId] = useState<ModuleId | null>(null);

  useEffect(() => {
    if (!slug) {
      setState({ status: "not-found" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    api.events
      .get(slug)
      .then((event) => {
        if (!cancelled) setState({ status: "ready", event });
      })
      .catch(() => {
        const seed = seededEvents[slug];
        if (cancelled) return;
        setState(seed ? { status: "ready", event: seed } : { status: "not-found" });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  function update(next: Event) {
    setState({ status: "ready", event: next });
    setDirty(true);
    setSave("idle");
  }

  function toggleModule(id: ModuleId) {
    if (state.status !== "ready") return;
    const event = state.event;
    const cur = event.modules[id];
    if (!cur) return;
    update({
      ...event,
      modules: { ...event.modules, [id]: { ...cur, enabled: !cur.enabled } },
    });
  }

  // Move a module up or down in the current event's modulesOrder by one
  // position. delta=-1 is up, delta=+1 is down. No-op at the endpoints.
  function moveModule(id: ModuleId, delta: -1 | 1) {
    if (state.status !== "ready") return;
    const order = [...state.event.modulesOrder];
    const idx = order.indexOf(id);
    const target = idx + delta;
    if (idx === -1 || target < 0 || target >= order.length) return;
    [order[idx], order[target]] = [order[target], order[idx]];
    update({ ...state.event, modulesOrder: order });
  }

  // Drop `dragId` immediately before `targetId`. Used by the
  // drag-and-drop drop handler. Arrow buttons use moveModule instead.
  function reorderBefore(dragId: ModuleId, targetId: ModuleId) {
    if (state.status !== "ready" || dragId === targetId) return;
    const order = state.event.modulesOrder.filter((id) => id !== dragId);
    const idx = order.indexOf(targetId);
    if (idx === -1) return;
    order.splice(idx, 0, dragId);
    update({ ...state.event, modulesOrder: order });
  }

  async function saveNow() {
    if (state.status !== "ready") return;
    setSave("saving");
    setErrorMsg("");
    try {
      const saved = await api.events.save(state.event.slug, state.event);
      setState({ status: "ready", event: saved });
      setSave("saved");
      setDirty(false);
      setTimeout(() => setSave("idle"), 1800);
    } catch (err) {
      setSave("error");
      setErrorMsg(err instanceof Error ? err.message : "Save failed.");
    }
  }

  // Count modules the event itself knows about, not the global registry
  // default. An event's modules map may include deprecated IDs that the
  // registry no longer ships; the "X modules enabled" indicator should
  // reflect what's actually on this event.
  const enabledCount = useMemo(() => {
    if (state.status !== "ready") return 0;
    return Object.values(state.event.modules).filter((m) => m?.enabled).length;
  }, [state]);

  // Memoize the rendered module ID order so it doesn't recompute on
  // every drag-over render. modulesOrder is stable during a drag.
  const orderedIds = useMemo(
    () => (state.status === "ready" ? orderedModuleIds(state.event.modulesOrder) : []),
    [state],
  );

  if (state.status === "loading") {
    return (
      <div className="grid h-96 place-items-center text-muted">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
      </div>
    );
  }
  if (state.status === "not-found") return <NotFoundView />;
  const event = state.event;

  return (
    <div className="mx-auto w-full max-w-5xl">
      <header className="mb-8">
        <Link
          to="/admin"
          className="text-xs uppercase tracking-[0.18em] text-muted hover:text-accent"
        >
          ← All events
        </Link>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="display-title text-[clamp(2rem,4vw,3rem)] leading-tight">
                {event.title}
              </h1>
              <StatusPill status={event.status} />
            </div>
            <p className="mt-1 text-sm text-muted">
              {enabledCount} module{enabledCount === 1 ? "" : "s"} enabled
              {event.status === "draft" ? (
                <>
                  {" · "}
                  <span className="text-amber-700 dark:text-amber-400">
                    visible only to admins until you publish
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <PublishButton
              status={event.status}
              onClick={() =>
                update({
                  ...event,
                  status: event.status === "published" ? "draft" : "published",
                })
              }
            />
            <SaveButton
              state={save}
              dirty={dirty}
              error={errorMsg}
              onClick={() => void saveNow()}
            />
            <Link
              to={`/admin/events/${event.slug}/dashboard`}
              className="inline-flex items-center gap-1.5 rounded-full border border-line/70 px-4 py-2 text-sm text-ink hover:border-accent hover:text-accent"
            >
              Dashboard
            </Link>
            <Link
              to={`/${event.slug}`}
              target="_blank"
              className="inline-flex items-center gap-1.5 rounded-full border border-line/70 px-4 py-2 text-sm text-ink hover:border-accent hover:text-accent"
            >
              Open public
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </header>

      <BannerEditor event={event} onChange={update} />

      <HeroEditor event={event} onChange={(next) => update({ ...event, ...next })} />

      <AgendaEditor event={event} onChange={(next) => update({ ...event, ...next })} />

      <LocationEditor event={event} onChange={(next) => update({ ...event, ...next })} />

      <CompetitionEditor event={event} onChange={(next) => update({ ...event, ...next })} />

      <JuniorEditor event={event} onChange={(next) => update({ ...event, ...next })} />

      <DonationEditor event={event} onChange={(next) => update({ ...event, ...next })} />

      <section className="mt-12 mb-4 flex items-end justify-between">
        <h2 className="font-display text-xl font-light text-ink">Modules</h2>
        <p className="text-xs text-subtle">Drag or use the arrows to reorder. Toggle to show.</p>
      </section>

      {/*
        The row order follows event.modulesOrder, not the global registry
        order. Any modules registered after this event was created are
        appended so they can still be toggled; reorder is mouse-drag
        (GripVertical handle) plus keyboard up/down arrows for parity
        with screen-reader / touch users.
      */}
      {/*
        Container-level onDragEnd is a safety net: if a drag ends without
        firing the handle's onDragEnd (cross-window drop, stopPropagation
        in a child, etc.), the row could stay dimmed forever. This keeps
        dragId / dropTargetId from getting stuck.
      */}
      <div
        className="grid gap-3"
        onDragEnd={() => {
          setDragId(null);
          setDropTargetId(null);
        }}
      >
        {orderedIds.map((id, idx, arr) => {
          const entry = moduleRegistry[id];
          if (!entry) return null;
          const config = event.modules[id];
          const present = Boolean(config);
          const enabled = Boolean(config?.enabled);
          const isDragging = dragId === id;
          const isDropTarget = dropTargetId === id && dragId !== null && dragId !== id;
          return (
            <div
              key={id}
              onDragOver={(e) => {
                if (dragId && dragId !== id) {
                  e.preventDefault();
                  if (dropTargetId !== id) setDropTargetId(id);
                }
              }}
              onDragLeave={() => {
                if (dropTargetId === id) setDropTargetId(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragId) reorderBefore(dragId, id);
                setDragId(null);
                setDropTargetId(null);
              }}
              className={cn(
                "flex items-start justify-between gap-4 rounded-2xl border p-5 transition-colors",
                enabled ? "border-accent/40 bg-surface-raised" : "border-line/60 bg-surface",
                !present && "opacity-60",
                isDragging && "opacity-40",
                isDropTarget && "border-t-2 border-t-accent",
              )}
            >
              <div className="flex items-start gap-3 flex-1">
                <div className="flex flex-col items-center gap-1 pt-0.5">
                  {/*
                    Idle state stays calm. Hover scales up, swaps to the
                    accent color, and adds a soft tinted halo so the
                    affordance is unmistakable. The isDragging branch
                    keeps the row "lit" for the full drag (the browser
                    drops :hover and :active the moment a drag is
                    recognized, so the React-state class is what carries
                    the visual state through to the drop).
                  */}
                  <span
                    draggable
                    aria-hidden="true"
                    onDragStart={() => setDragId(id)}
                    onDragEnd={() => {
                      setDragId(null);
                      setDropTargetId(null);
                    }}
                    className={cn(
                      "inline-grid place-items-center rounded p-1 cursor-grab transition-all duration-200",
                      "text-subtle hover:text-accent hover:scale-125 hover:bg-accent/10",
                      "active:cursor-grabbing",
                      isDragging && "text-accent scale-125 bg-accent/15",
                    )}
                  >
                    <GripVertical className="h-4 w-4" strokeWidth={1.75} />
                  </span>
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => moveModule(id, -1)}
                      disabled={idx === 0}
                      aria-label={`Move ${entry.label} up`}
                      className="focus-ring rounded text-subtle hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronUp className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveModule(id, 1)}
                      disabled={idx === arr.length - 1}
                      aria-label={`Move ${entry.label} down`}
                      className="focus-ring rounded text-subtle hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-ink">{entry.label}</h3>
                    {!present ? (
                      <span className="rounded-full border border-line/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-subtle">
                        Not configured
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm text-muted">{entry.description}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggleModule(id)}
                disabled={!present}
                aria-pressed={enabled}
                className={cn(
                  "relative h-9 w-16 shrink-0 rounded-full border transition-colors",
                  enabled ? "border-accent bg-accent" : "border-line/70 bg-surface",
                  !present && "cursor-not-allowed",
                )}
              >
                <span
                  className={cn(
                    "absolute top-1 left-1 grid h-7 w-7 place-items-center rounded-full bg-surface-raised transition-transform shadow",
                    enabled ? "translate-x-7 text-accent-ink" : "translate-x-0 text-muted",
                  )}
                >
                  <Power className="h-3.5 w-3.5" strokeWidth={1.75} />
                </span>
              </button>
            </div>
          );
        })}
      </div>

      <p className="mt-12 text-center text-xs text-subtle">Changes save to KV.</p>
    </div>
  );
}

// Returns the module IDs to render in the admin list: the event's saved
// order first, then any registry IDs the event hasn't seen yet (so newly
// added modules still show up as togglable rows). Duplicates and unknown
// IDs are filtered.
function orderedModuleIds(saved: ModuleId[]): ModuleId[] {
  const known = new Set<ModuleId>(Object.keys(moduleRegistry) as ModuleId[]);
  const seen = new Set<ModuleId>();
  const result: ModuleId[] = [];
  for (const id of saved) {
    if (known.has(id) && !seen.has(id)) {
      result.push(id);
      seen.add(id);
    }
  }
  for (const id of known) {
    if (!seen.has(id)) {
      result.push(id);
      seen.add(id);
    }
  }
  return result;
}

function SaveButton({
  state,
  dirty,
  error,
  onClick,
}: {
  state: SaveState;
  dirty: boolean;
  error: string;
  onClick: () => void;
}) {
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-soft px-4 py-2 text-sm font-medium text-accent-ink dark:text-accent">
        <Check className="h-4 w-4" strokeWidth={1.75} />
        Saved
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!dirty || state === "saving"}
      title={error || undefined}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-all",
        dirty
          ? "bg-ink text-canvas dark:bg-accent dark:text-accent-on shadow-glow hover:bg-ink/90 dark:hover:bg-accent/90"
          : "bg-ink/30 text-canvas cursor-not-allowed",
      )}
    >
      {state === "saving" ? (
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
      ) : (
        <Save className="h-4 w-4" strokeWidth={1.75} />
      )}
      {state === "error" ? "Retry save" : "Save"}
    </button>
  );
}

function StatusPill({ status }: { status: "draft" | "published" }) {
  if (status === "published") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] uppercase tracking-[0.14em] text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Published
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] uppercase tracking-[0.14em] text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
      Draft
    </span>
  );
}

function PublishButton({
  status,
  onClick,
}: {
  status: "draft" | "published";
  onClick: () => void;
}) {
  const isPublished = status === "published";
  return (
    <button
      type="button"
      onClick={onClick}
      title={
        isPublished
          ? "Unpublish: hides the event from the public site."
          : "Publish: make the event visible to anyone with the link."
      }
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors",
        isPublished
          ? "border border-line/70 text-ink hover:border-amber-400 hover:text-amber-700 dark:hover:text-amber-300"
          : "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-400",
      )}
    >
      {isPublished ? "Unpublish" : "Publish"}
    </button>
  );
}

function HeroEditor({
  event,
  onChange,
}: {
  event: Event;
  onChange: (partial: { modules: Event["modules"]; title?: string; tagline?: string }) => void;
}) {
  const maybeHero = event.modules.hero;
  if (!maybeHero) return null;
  const hero = maybeHero;

  function setHero(patch: Partial<NonNullable<Event["modules"]["hero"]>>) {
    onChange({
      title: event.title,
      tagline: event.tagline,
      modules: { ...event.modules, hero: { ...hero, ...patch } },
    });
  }

  return (
    <section className="surface-card p-6 mb-8">
      <h2 className="font-display text-xl font-light text-ink">Hero</h2>
      <p className="mt-1 text-sm text-muted">
        Wrap a word in <code>*asterisks*</code> in the title to highlight it in gold.
      </p>
      <div className="mt-6 grid gap-5">
        <Row>
          <Field label="Event title">
            <input
              type="text"
              value={event.title}
              onChange={(e) =>
                onChange({
                  title: e.target.value,
                  tagline: event.tagline,
                  modules: event.modules,
                })
              }
              className="form-input"
            />
          </Field>
          <Field label="Eyebrow (small line above title)">
            <input
              type="text"
              value={hero.eyebrow ?? ""}
              onChange={(e) => setHero({ eyebrow: e.target.value })}
              className="form-input"
            />
          </Field>
        </Row>
        <Field label="Hero title (display)">
          <input
            type="text"
            value={hero.title}
            onChange={(e) => setHero({ title: e.target.value })}
            className="form-input"
            placeholder="A festival in honor of *Imam Ali* (AS)"
          />
        </Field>
        <Field label="Subtitle">
          <textarea
            rows={2}
            value={hero.subtitle ?? ""}
            onChange={(e) => setHero({ subtitle: e.target.value })}
            className="form-input"
          />
        </Field>
        <Row>
          <Field label="Date">
            <input
              type="text"
              value={hero.date}
              onChange={(e) => setHero({ date: e.target.value })}
              className="form-input"
              placeholder="Saturday, January 3rd · 2026"
            />
          </Field>
          <Field label="Location">
            <input
              type="text"
              value={hero.location ?? ""}
              onChange={(e) => setHero({ location: e.target.value })}
              className="form-input"
              placeholder="Mobeen Center · Main Hall"
            />
          </Field>
        </Row>
      </div>
    </section>
  );
}

function AgendaEditor({
  event,
  onChange,
}: {
  event: Event;
  onChange: (partial: { modules: Event["modules"] }) => void;
}) {
  const maybeAgenda = event.modules.agenda;
  if (!maybeAgenda) return null;
  const agenda = maybeAgenda;

  function setAgenda(patch: Partial<NonNullable<Event["modules"]["agenda"]>>) {
    onChange({ modules: { ...event.modules, agenda: { ...agenda, ...patch } } });
  }

  function updateItem(idx: number, patch: Partial<(typeof agenda.items)[number]>) {
    const items = agenda.items.slice();
    items[idx] = { ...items[idx], ...patch };
    setAgenda({ items });
  }
  function addItem() {
    setAgenda({
      items: [
        ...agenda.items,
        { start: "12:00 PM", end: "12:30 PM", title: "New item", icon: "speech" },
      ],
    });
  }
  function removeItem(idx: number) {
    setAgenda({ items: agenda.items.filter((_, i) => i !== idx) });
  }
  function move(idx: number, delta: number) {
    const items = agenda.items.slice();
    const target = idx + delta;
    if (target < 0 || target >= items.length) return;
    [items[idx], items[target]] = [items[target], items[idx]];
    setAgenda({ items });
  }

  const iconOptions: Array<NonNullable<(typeof agenda.items)[number]["icon"]>> = [
    "prayer",
    "speech",
    "meal",
    "poem",
    "game",
    "award",
    "kids",
    "music",
  ];

  return (
    <section className="surface-card p-6 mb-8">
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-display text-xl font-light text-ink">Agenda</h2>
        <button
          type="button"
          onClick={addItem}
          className="rounded-full border border-line/70 px-3.5 py-1.5 text-xs uppercase tracking-[0.14em] text-muted hover:border-accent hover:text-accent"
        >
          + Add row
        </button>
      </div>

      <Row className="mt-4">
        <Field label="Section title">
          <input
            type="text"
            value={agenda.title ?? ""}
            onChange={(e) => setAgenda({ title: e.target.value })}
            className="form-input"
          />
        </Field>
        <Field label="Date label">
          <input
            type="text"
            value={agenda.dateLabel ?? ""}
            onChange={(e) => setAgenda({ dateLabel: e.target.value })}
            className="form-input"
          />
        </Field>
      </Row>

      <ol className="mt-6 grid gap-3">
        {agenda.items.map((item, i) => (
          <li key={i} className="rounded-2xl border border-line/60 bg-surface p-4 grid gap-3">
            <div className="grid gap-3 sm:grid-cols-[110px,110px,1fr,140px,auto]">
              <input
                type="text"
                value={item.start}
                onChange={(e) => updateItem(i, { start: e.target.value })}
                className="form-input"
                placeholder="2:00 PM"
              />
              <input
                type="text"
                value={item.end ?? ""}
                onChange={(e) => updateItem(i, { end: e.target.value })}
                className="form-input"
                placeholder="3:00 PM"
              />
              <input
                type="text"
                value={item.title}
                onChange={(e) => updateItem(i, { title: e.target.value })}
                className="form-input"
                placeholder="Title"
              />
              <select
                value={item.icon ?? "speech"}
                onChange={(e) =>
                  updateItem(i, { icon: e.target.value as (typeof iconOptions)[number] })
                }
                className="form-input"
              >
                {iconOptions.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <div className="flex items-center justify-end gap-1.5">
                <IconButton onClick={() => move(i, -1)} label="↑" />
                <IconButton onClick={() => move(i, 1)} label="↓" />
                <IconButton onClick={() => removeItem(i)} label="✕" tone="danger" />
              </div>
            </div>
            <textarea
              rows={1}
              value={item.description ?? ""}
              onChange={(e) => updateItem(i, { description: e.target.value })}
              className="form-input"
              placeholder="Optional description"
            />
            <label className="inline-flex items-center gap-2 text-xs text-muted">
              <input
                type="checkbox"
                checked={Boolean(item.highlight)}
                onChange={(e) => updateItem(i, { highlight: e.target.checked })}
                className="h-4 w-4 accent-accent"
              />
              Highlight as parallel
            </label>
          </li>
        ))}
      </ol>
    </section>
  );
}

// Slugify a field id so it stays a usable form key regardless of what
// the admin types. Lowercase, replace runs of non-alphanumeric with a
// single underscore, trim leading/trailing underscores. Empty input
// returns "" so the user sees what happened.
function slugifyFieldId(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Mint a unique field id when the admin clicks "+ Add field". Using a
// random suffix avoids collisions when the admin adds, removes, and
// re-adds rows (`field${len+1}` could repeat an existing id).
function mintFieldId(): string {
  const rand = (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random()}`)
    .replace(/-/g, "")
    .slice(0, 6);
  return `field_${rand}`;
}

// Editor for the per-event "Community competition" module: a generic
// contest configurable as a bake-off, art show, recitation, etc. The
// module is generic enough that the public renderer doesn't bake in a
// noun; the admin sets `entryNoun` and `participantNoun` and the public
// page does the substitution.
function CompetitionEditor({
  event,
  onChange,
}: {
  event: Event;
  onChange: (partial: { modules: Event["modules"] }) => void;
}) {
  const maybeComp = event.modules.competition;
  if (!maybeComp) return null;
  const comp = maybeComp;

  function setComp(patch: Partial<NonNullable<Event["modules"]["competition"]>>) {
    onChange({ modules: { ...event.modules, competition: { ...comp, ...patch } } });
  }

  function updateField(idx: number, patch: Partial<(typeof comp.fields)[number]>) {
    const fields = comp.fields.slice();
    fields[idx] = { ...fields[idx], ...patch };
    setComp({ fields });
  }
  function addField() {
    setComp({
      fields: [
        ...comp.fields,
        { id: mintFieldId(), label: "New field", type: "text", publiclyVisible: true },
      ],
    });
  }
  function removeField(idx: number) {
    setComp({ fields: comp.fields.filter((_, i) => i !== idx) });
  }
  function moveField(idx: number, delta: number) {
    const fields = comp.fields.slice();
    const target = idx + delta;
    if (target < 0 || target >= fields.length) return;
    [fields[idx], fields[target]] = [fields[target], fields[idx]];
    setComp({ fields });
  }

  // `satisfies` makes the select options exhaustive: if a new member is
  // added to the schema's `type` union (e.g. "number"), TypeScript will
  // error here until this list is updated and the new <option> renders.
  const fieldTypes = ["text", "longtext", "boolean"] as const satisfies ReadonlyArray<
    (typeof comp.fields)[number]["type"]
  >;

  return (
    <section className="surface-card p-6 mb-8">
      <h2 className="font-display text-xl font-light text-ink">Competition</h2>
      <p className="mt-1 text-sm text-muted">
        Generic contest: configure the nouns and the entry form fields.
      </p>

      <div className="mt-6 grid gap-5">
        <Row>
          <Field label="Section title">
            <input
              type="text"
              value={comp.title}
              onChange={(e) => setComp({ title: e.target.value })}
              className="form-input"
            />
          </Field>
          <Field label="Kind label (free-form)">
            <input
              type="text"
              value={comp.kind ?? ""}
              onChange={(e) => setComp({ kind: e.target.value || undefined })}
              className="form-input"
              placeholder="Cake competition"
            />
          </Field>
        </Row>

        <Field label="Description">
          <textarea
            rows={3}
            value={comp.description ?? ""}
            onChange={(e) => setComp({ description: e.target.value || undefined })}
            className="form-input"
            placeholder="A line or two introducing the contest."
          />
        </Field>

        <Row>
          <Field label="Entry noun (singular)">
            <input
              type="text"
              value={comp.entryNoun}
              onChange={(e) => setComp({ entryNoun: e.target.value })}
              className="form-input"
              placeholder="cake"
            />
          </Field>
          <Field label="Participant noun (singular)">
            <input
              type="text"
              value={comp.participantNoun}
              onChange={(e) => setComp({ participantNoun: e.target.value })}
              className="form-input"
              placeholder="baker"
            />
          </Field>
        </Row>

        <Row>
          <Field label="Judging mode">
            <select
              value={comp.judgingMode}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "ranking" || v === "score") setComp({ judgingMode: v });
              }}
              className="form-input"
            >
              <option value="ranking">Ranking</option>
              <option value="score">Score</option>
            </select>
          </Field>
          <Field label="Show entries publicly">
            <label className="mt-1 inline-flex items-center gap-2 text-sm text-muted">
              <input
                type="checkbox"
                checked={Boolean(comp.showEntries)}
                onChange={(e) => setComp({ showEntries: e.target.checked })}
                className="h-4 w-4 accent-accent"
              />
              List registered entries on the public page
            </label>
          </Field>
        </Row>
      </div>

      <div className="mt-10 flex items-center justify-between gap-4">
        <h3 className="font-medium text-ink">Entry form fields</h3>
        <button
          type="button"
          onClick={addField}
          className="rounded-full border border-line/70 px-3.5 py-1.5 text-xs uppercase tracking-[0.14em] text-muted hover:border-accent hover:text-accent"
        >
          + Add field
        </button>
      </div>
      {/*
        The field ID is the storage key under which each submission's
        value is recorded. Renaming after entries exist orphans the
        old key on every saved entry; the input slugifies aggressively
        so the admin can't accidentally produce a key that breaks the
        public submission handler.
      */}
      <p className="mt-2 text-xs text-subtle">
        Field IDs become the storage key for each submission. Changing an ID after entries are
        collected will orphan saved values for that field.
      </p>

      <ol className="mt-4 grid gap-3">
        {comp.fields.length === 0 ? (
          <li className="rounded-2xl border border-dashed border-line/60 bg-surface p-4 text-sm text-subtle text-center">
            No fields yet. Without at least one field the entry form will be empty; add a field
            above to make the contest accept entries.
          </li>
        ) : null}
        {comp.fields.map((field, i, arr) => (
          <li
            key={field.id}
            className="rounded-2xl border border-line/60 bg-surface p-4 grid gap-3"
          >
            <div className="grid gap-3 sm:grid-cols-[140px,1fr,130px,auto]">
              <input
                type="text"
                value={field.id}
                onChange={(e) => updateField(i, { id: slugifyFieldId(e.target.value) })}
                className="form-input font-mono text-xs"
                placeholder="field_id"
                aria-label={`Field ${i + 1} ID`}
              />
              <input
                type="text"
                value={field.label}
                onChange={(e) => updateField(i, { label: e.target.value })}
                className="form-input"
                placeholder="Label shown to entrants"
                aria-label={`Field ${i + 1} label`}
              />
              <select
                value={field.type}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "text" || v === "longtext" || v === "boolean") {
                    updateField(i, { type: v });
                  }
                }}
                className="form-input"
                aria-label={`Field ${i + 1} type`}
              >
                {fieldTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <div className="flex items-center justify-end gap-1.5">
                <IconButton
                  onClick={() => moveField(i, -1)}
                  label="↑"
                  ariaLabel={`Move field ${i + 1} up`}
                  disabled={i === 0}
                />
                <IconButton
                  onClick={() => moveField(i, 1)}
                  label="↓"
                  ariaLabel={`Move field ${i + 1} down`}
                  disabled={i === arr.length - 1}
                />
                <IconButton
                  onClick={() => removeField(i)}
                  label="✕"
                  tone="danger"
                  ariaLabel={`Remove field ${i + 1}`}
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                type="text"
                value={field.placeholder ?? ""}
                onChange={(e) => updateField(i, { placeholder: e.target.value || undefined })}
                className="form-input"
                placeholder="Placeholder (optional)"
                aria-label={`Field ${i + 1} placeholder`}
              />
              <input
                type="text"
                value={field.hint ?? ""}
                onChange={(e) => updateField(i, { hint: e.target.value || undefined })}
                className="form-input"
                placeholder="Hint shown under the field (optional)"
                aria-label={`Field ${i + 1} hint`}
              />
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(field.required)}
                  onChange={(e) => updateField(i, { required: e.target.checked })}
                  className="h-4 w-4 accent-accent"
                />
                Required
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(field.publiclyVisible)}
                  onChange={(e) => updateField(i, { publiclyVisible: e.target.checked })}
                  className="h-4 w-4 accent-accent"
                />
                Show on public entries list
              </label>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// Mint a short client-side UI key. Age groups and stations have no
// schema id, so we maintain a parallel keys array for stable React
// keys during reorder. These never persist to the server.
function mintUiKey(): string {
  const r = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random()}`;
  return r.replace(/-/g, "").slice(0, 8);
}

// Resize a UI-keys array to match a target length. Drops trailing keys
// when shrinking; appends fresh ones when growing. Returns the same
// array reference when no change is needed so React skips the rerender.
function syncKeys(prev: string[], target: number): string[] {
  if (prev.length === target) return prev;
  if (prev.length > target) return prev.slice(0, target);
  const next = prev.slice();
  while (next.length < target) next.push(mintUiKey());
  return next;
}

// Editor for the "Junior program" module: a parallel program for kids
// during the event. Captures the overall message plus two independent
// arrays: age groups (a child slots into one) and stations (a child
// rotates through all of them).
function JuniorEditor({
  event,
  onChange,
}: {
  event: Event;
  onChange: (partial: { modules: Event["modules"] }) => void;
}) {
  const maybeJunior = event.modules.junior;
  // Parallel UI key arrays kept length-synced with the data arrays.
  // Index keys would corrupt the input cursor / focus during reorder;
  // these stable per-row IDs let React reuse the right DOM node.
  // useState's lazy initializer fires once per mount; if the editor is
  // ever passed a different junior object (event reload), the effect
  // below resyncs lengths.
  const [groupKeys, setGroupKeys] = useState<string[]>(() =>
    (maybeJunior?.ageGroups ?? []).map(mintUiKey),
  );
  const [stationKeys, setStationKeys] = useState<string[]>(() =>
    (maybeJunior?.stations ?? []).map(mintUiKey),
  );
  // Keep the key arrays length-synced if the data lengths change from
  // outside this component (event reload, undo from elsewhere). Extra
  // keys are dropped from the end; missing keys are appended.
  useEffect(() => {
    const groupLen = maybeJunior?.ageGroups.length ?? 0;
    setGroupKeys((prev) => syncKeys(prev, groupLen));
    const stationLen = maybeJunior?.stations?.length ?? 0;
    setStationKeys((prev) => syncKeys(prev, stationLen));
  }, [maybeJunior?.ageGroups.length, maybeJunior?.stations?.length]);

  if (!maybeJunior) return null;
  const junior = maybeJunior;

  function setJunior(patch: Partial<NonNullable<Event["modules"]["junior"]>>) {
    onChange({ modules: { ...event.modules, junior: { ...junior, ...patch } } });
  }

  function updateGroup(idx: number, patch: Partial<(typeof junior.ageGroups)[number]>) {
    const ageGroups = junior.ageGroups.slice();
    ageGroups[idx] = { ...ageGroups[idx], ...patch };
    setJunior({ ageGroups });
  }
  function addGroup() {
    setJunior({
      ageGroups: [...junior.ageGroups, { label: "New age group", description: "" }],
    });
    setGroupKeys((prev) => [...prev, mintUiKey()]);
  }
  function removeGroup(idx: number) {
    setJunior({ ageGroups: junior.ageGroups.filter((_, i) => i !== idx) });
    setGroupKeys((prev) => prev.filter((_, i) => i !== idx));
  }
  function moveGroup(idx: number, delta: number) {
    const target = idx + delta;
    if (target < 0 || target >= junior.ageGroups.length) return;
    const ageGroups = junior.ageGroups.slice();
    [ageGroups[idx], ageGroups[target]] = [ageGroups[target], ageGroups[idx]];
    setJunior({ ageGroups });
    setGroupKeys((prev) => {
      const next = prev.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  // Stations live in a separate array; an empty/missing array is normal
  // for events that group by age only.
  const stations = junior.stations ?? [];
  function updateStation(idx: number, patch: Partial<(typeof stations)[number]>) {
    const next = stations.slice();
    next[idx] = { ...next[idx], ...patch };
    setJunior({ stations: next });
  }
  function addStation() {
    setJunior({ stations: [...stations, { name: "New station", description: "" }] });
    setStationKeys((prev) => [...prev, mintUiKey()]);
  }
  function removeStation(idx: number) {
    setJunior({ stations: stations.filter((_, i) => i !== idx) });
    setStationKeys((prev) => prev.filter((_, i) => i !== idx));
  }
  function moveStation(idx: number, delta: number) {
    const target = idx + delta;
    if (target < 0 || target >= stations.length) return;
    const next = stations.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setJunior({ stations: next });
    setStationKeys((prev) => {
      const out = prev.slice();
      [out[idx], out[target]] = [out[target], out[idx]];
      return out;
    });
  }

  return (
    <section className="surface-card p-6 mb-8">
      <h2 className="font-display text-xl font-light text-ink">Junior program</h2>
      <p className="mt-1 text-sm text-muted">
        Overall message, age groups, and stations for the parallel kids' program.
      </p>

      <div className="mt-6 grid gap-5">
        <Field label="Section title">
          <input
            type="text"
            value={junior.title}
            onChange={(e) => setJunior({ title: e.target.value })}
            className="form-input"
          />
        </Field>
        <Field label="Description (overall theme / parent-facing message)">
          <textarea
            rows={3}
            value={junior.description ?? ""}
            onChange={(e) => setJunior({ description: e.target.value || undefined })}
            className="form-input"
            placeholder="What the program is, what parents should expect."
          />
        </Field>
      </div>

      <div className="mt-10 flex items-center justify-between gap-4">
        <h3 className="font-medium text-ink">Age groups</h3>
        <button
          type="button"
          onClick={addGroup}
          className="rounded-full border border-line/70 px-3.5 py-1.5 text-xs uppercase tracking-[0.14em] text-muted hover:border-accent hover:text-accent"
        >
          + Add group
        </button>
      </div>
      <ol className="mt-4 grid gap-3">
        {junior.ageGroups.length === 0 ? (
          <li className="rounded-2xl border border-dashed border-line/60 bg-surface p-4 text-sm text-subtle text-center">
            No age groups yet. Add at least one so families know where their child fits.
          </li>
        ) : null}
        {junior.ageGroups.map((group, i, arr) => (
          <li
            key={groupKeys[i] ?? `g-${i}`}
            className="rounded-2xl border border-line/60 bg-surface p-4 grid gap-3 sm:grid-cols-[160px,1fr,auto]"
          >
            <input
              type="text"
              value={group.label}
              onChange={(e) => updateGroup(i, { label: e.target.value })}
              className="form-input"
              placeholder="3 - 5"
              aria-label={`Age group ${i + 1} label`}
            />
            <input
              type="text"
              value={group.description ?? ""}
              onChange={(e) => updateGroup(i, { description: e.target.value || undefined })}
              className="form-input"
              placeholder="What this group does"
              aria-label={`Age group ${i + 1} description`}
            />
            <div className="flex items-center justify-end gap-1.5">
              <IconButton
                onClick={() => moveGroup(i, -1)}
                label="↑"
                ariaLabel={`Move age group ${i + 1} up`}
                disabled={i === 0}
              />
              <IconButton
                onClick={() => moveGroup(i, 1)}
                label="↓"
                ariaLabel={`Move age group ${i + 1} down`}
                disabled={i === arr.length - 1}
              />
              <IconButton
                onClick={() => removeGroup(i)}
                label="✕"
                tone="danger"
                ariaLabel={`Remove age group ${i + 1}`}
              />
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-10 flex items-center justify-between gap-4">
        <h3 className="font-medium text-ink">Activities / stations</h3>
        <button
          type="button"
          onClick={addStation}
          className="rounded-full border border-line/70 px-3.5 py-1.5 text-xs uppercase tracking-[0.14em] text-muted hover:border-accent hover:text-accent"
        >
          + Add station
        </button>
      </div>
      <ol className="mt-4 grid gap-3">
        {stations.length === 0 ? (
          <li className="rounded-2xl border border-dashed border-line/60 bg-surface p-4 text-sm text-subtle text-center">
            No stations yet. Stations are optional, useful when the program rotates kids through
            multiple activities.
          </li>
        ) : null}
        {stations.map((station, i, arr) => (
          <li
            key={stationKeys[i] ?? `s-${i}`}
            className="rounded-2xl border border-line/60 bg-surface p-4 grid gap-3 sm:grid-cols-[200px,1fr,auto]"
          >
            <input
              type="text"
              value={station.name}
              onChange={(e) => updateStation(i, { name: e.target.value })}
              className="form-input"
              placeholder="Calligraphy corner"
              aria-label={`Station ${i + 1} name`}
            />
            <input
              type="text"
              value={station.description ?? ""}
              onChange={(e) => updateStation(i, { description: e.target.value || undefined })}
              className="form-input"
              placeholder="What happens at this station"
              aria-label={`Station ${i + 1} description`}
            />
            <div className="flex items-center justify-end gap-1.5">
              <IconButton
                onClick={() => moveStation(i, -1)}
                label="↑"
                ariaLabel={`Move station ${i + 1} up`}
                disabled={i === 0}
              />
              <IconButton
                onClick={() => moveStation(i, 1)}
                label="↓"
                ariaLabel={`Move station ${i + 1} down`}
                disabled={i === arr.length - 1}
              />
              <IconButton
                onClick={() => removeStation(i)}
                label="✕"
                tone="danger"
                ariaLabel={`Remove station ${i + 1}`}
              />
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// `enabled: false` hides the donation module entirely; `stripeReady`
// gates only the public donate button so an admin can preview a
// configured card without exposing a live checkout.
function DonationEditor({
  event,
  onChange,
}: {
  event: Event;
  onChange: (partial: { modules: Event["modules"] }) => void;
}) {
  const maybeDonation = event.modules.donation;
  if (!maybeDonation) return null;
  const donation = maybeDonation;

  function setDonation(patch: Partial<NonNullable<Event["modules"]["donation"]>>) {
    onChange({ modules: { ...event.modules, donation: { ...donation, ...patch } } });
  }

  function setAmountAt(idx: number, raw: string) {
    // Allow 0 transiently so the admin can clear a field and type a new
    // value without it snapping to 1 on every keystroke. The blur
    // handler below clamps to a minimum of 1 before commit.
    const n = Math.max(0, Math.round(Number(raw) || 0));
    const amounts = donation.suggestedAmounts.slice();
    amounts[idx] = n;
    setDonation({ suggestedAmounts: amounts });
  }
  function clampAmountAt(idx: number) {
    if (donation.suggestedAmounts[idx] >= 1) return;
    // Floor at $1 on blur to match the checkout endpoint's
    // MIN_AMOUNT_CENTS=100 guard; 0 would round-trip into a Stripe
    // rejection on submit.
    const amounts = donation.suggestedAmounts.slice();
    amounts[idx] = 1;
    setDonation({ suggestedAmounts: amounts });
  }
  function removeAmount(idx: number) {
    if (donation.suggestedAmounts.length <= 1) return;
    setDonation({ suggestedAmounts: donation.suggestedAmounts.filter((_, i) => i !== idx) });
  }
  function addAmount() {
    if (donation.suggestedAmounts.length >= 6) return;
    const last = donation.suggestedAmounts[donation.suggestedAmounts.length - 1];
    const next = typeof last === "number" && last > 0 ? last * 2 : 100;
    setDonation({ suggestedAmounts: [...donation.suggestedAmounts, next] });
  }

  const goalDollars = donation.goalCents ? String(Math.round(donation.goalCents / 100)) : "";

  return (
    <section className="surface-card p-6 mb-8">
      <h2 className="font-display text-xl font-light text-ink">Donation campaign</h2>
      <p className="mt-1 text-sm text-muted">
        Per-event giving with suggested amounts and an optional fundraising goal. Toggle the module
        on/off in the list below; this panel controls the content and the Stripe gate.
      </p>

      <div className="mt-6 grid gap-5">
        <Field label="Section title">
          <input
            type="text"
            value={donation.title}
            onChange={(e) => setDonation({ title: e.target.value })}
            className="form-input"
          />
        </Field>

        <Field label="Description">
          <textarea
            rows={3}
            value={donation.description ?? ""}
            onChange={(e) => setDonation({ description: e.target.value || undefined })}
            className="form-input"
            placeholder="Short paragraph under the headline."
          />
        </Field>

        <Row>
          <Field label="Fundraising target (optional, USD)">
            <div className="mt-1.5 inline-flex items-center gap-1 rounded-xl border border-line/70 bg-surface px-3 py-2">
              <span className="text-sm text-muted">$</span>
              <input
                type="number"
                min={0}
                step={100}
                placeholder="0"
                value={goalDollars}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  if (!v) {
                    setDonation({ goalCents: undefined });
                    return;
                  }
                  const n = Math.max(0, Math.round(Number(v) || 0));
                  setDonation({ goalCents: n === 0 ? undefined : n * 100 });
                }}
                className="w-28 bg-transparent text-sm text-ink outline-none"
                aria-label="Fundraising target in whole dollars"
              />
            </div>
            <p className="mt-1 text-xs text-subtle">
              Leave blank for open-ended giving (no progress bar percentage).
            </p>
          </Field>
          {/* Plain div not Field: a label wrapping a button doesn't form a
              valid HTML label association. The aria-label on the button
              is the actual accessible name. */}
          <div>
            <p className="block text-sm font-medium text-ink">Allow custom amount</p>
            <button
              type="button"
              onClick={() => setDonation({ allowCustom: !donation.allowCustom })}
              aria-pressed={donation.allowCustom}
              aria-label="Allow custom amount"
              className={cn(
                "relative mt-1.5 h-9 w-16 shrink-0 rounded-full border transition-colors",
                donation.allowCustom ? "border-accent bg-accent" : "border-line/70 bg-surface",
              )}
            >
              <span
                className={cn(
                  "absolute top-1 left-1 grid h-7 w-7 place-items-center rounded-full bg-surface-raised transition-transform shadow",
                  donation.allowCustom
                    ? "translate-x-7 text-accent-ink"
                    : "translate-x-0 text-muted",
                )}
              >
                <Power className="h-3.5 w-3.5" strokeWidth={1.75} />
              </span>
            </button>
            <p className="mt-1 text-xs text-subtle">
              Adds an &ldquo;Other&rdquo; tile that opens a free-form amount input.
            </p>
          </div>
        </Row>

        <div>
          <p className="text-sm font-medium text-ink">Suggested amounts (USD)</p>
          <p className="mt-1 text-xs text-subtle">1 to 6 tiles. Whole dollars.</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {/* Key by index, not by value: the value changes on every
                keystroke while editing, and a value-in-key would remount
                the row and drop input focus on each character. */}
            {donation.suggestedAmounts.map((amt, idx) => (
              <div
                key={idx}
                className="inline-flex items-center gap-1 rounded-full border border-line/70 bg-surface px-3 py-1.5"
              >
                <span className="text-sm text-muted">$</span>
                <input
                  type="number"
                  min={1}
                  value={amt}
                  onChange={(e) => setAmountAt(idx, e.target.value)}
                  onBlur={() => clampAmountAt(idx)}
                  className="w-16 bg-transparent text-sm text-ink outline-none"
                  aria-label={`Suggested amount ${idx + 1} in whole dollars`}
                />
                <button
                  type="button"
                  onClick={() => removeAmount(idx)}
                  disabled={donation.suggestedAmounts.length <= 1}
                  aria-label={`Remove $${amt}`}
                  className="text-subtle hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addAmount}
              disabled={donation.suggestedAmounts.length >= 6}
              className="inline-flex items-center rounded-full border border-line/70 px-3 py-1.5 text-sm text-muted hover:border-accent hover:text-accent disabled:opacity-40 disabled:cursor-not-allowed"
            >
              + Add
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-line/70 bg-surface p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-ink">Stripe checkout ready</p>
              <p className="mt-1 text-xs text-muted">
                Independent of the module&rsquo;s on/off toggle. When off, visitors see
                &ldquo;Checkout coming soon&rdquo; and the donate button stays disabled. Flip on
                only after a $1 dry-run on this campaign has cleared.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDonation({ stripeReady: !donation.stripeReady })}
              aria-pressed={Boolean(donation.stripeReady)}
              aria-label="Stripe checkout ready"
              className={cn(
                "relative h-9 w-16 shrink-0 rounded-full border transition-colors",
                donation.stripeReady
                  ? "border-accent bg-accent"
                  : "border-line/70 bg-surface-raised",
              )}
            >
              <span
                className={cn(
                  "absolute top-1 left-1 grid h-7 w-7 place-items-center rounded-full bg-surface-raised transition-transform shadow",
                  donation.stripeReady
                    ? "translate-x-7 text-accent-ink"
                    : "translate-x-0 text-muted",
                )}
              >
                <Power className="h-3.5 w-3.5" strokeWidth={1.75} />
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("grid gap-5 sm:grid-cols-2", className)}>{children}</div>;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  // Callers always render a form control inside `children`, making it a
  // descendant of the label. That's an HTML-spec-valid association,
  // but biome can't trace React composition to verify it.
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: control is rendered as a descendant via children
    <label className="block">
      <span className="block text-sm font-medium text-ink">{label}</span>
      {children}
    </label>
  );
}

function BannerEditor({
  event,
  onChange,
}: {
  event: Event;
  onChange: (next: Event) => void;
}) {
  const maybeHero = event.modules.hero;
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<"idle" | "processing" | "uploading" | "done" | "error">(
    "idle",
  );
  const [error, setError] = useState("");
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [suggested, setSuggested] = useState<ExtractedTheme | null>(null);
  const mode = (event.theme?.mode ?? "joyful") as "joyful" | "mourning";

  if (!maybeHero) return null;
  const hero = maybeHero;
  const bannerSrc = previewSrc ?? hero.banner?.src ?? null;

  async function handleFile(file: File) {
    setError("");
    setPhase("processing");
    try {
      const pipe = await processBanner(file, { alt: event.title, mode });
      setPreviewSrc(pipe.preview);
      setSuggested(pipe.theme);
      setPhase("uploading");
      const banner = await uploadBanner(event.slug, pipe);
      setPhase("done");
      const nextHero = {
        ...hero,
        banner: {
          src: banner.src,
          alt: banner.alt,
          variants: banner.variants,
        },
      };

      // Preserve admin's hand-picked theme. Only auto-apply the extracted
      // palette when no primary color has been chosen yet for this event.
      const themeAlreadyChosen = Boolean(event.theme?.primary);
      const nextTheme = themeAlreadyChosen
        ? event.theme
        : {
            ...event.theme,
            primary: pipe.theme.primary,
            accent: pipe.theme.accent,
          };

      onChange({
        ...event,
        modules: { ...event.modules, hero: nextHero },
        theme: nextTheme,
      });
    } catch (err) {
      setPhase("error");
      setError(err instanceof Error ? err.message : "Upload failed.");
    }
  }

  function setMode(next: "joyful" | "mourning") {
    onChange({
      ...event,
      theme: { ...event.theme, mode: next },
    });
  }

  function setSwatch(hex: string, role: "primary" | "accent") {
    // Don't force `mode` here; toggling the palette shouldn't side-effect
    // the joyful/mourning state. The Joyful/Mourning pills own that field.
    onChange({
      ...event,
      theme: { ...event.theme, [role]: hex },
    });
  }

  return (
    <section className="surface-card overflow-hidden mb-8">
      <div className="grid gap-0 sm:grid-cols-[240px,1fr]">
        <div className="relative aspect-[3/4] sm:aspect-auto bg-paper">
          {bannerSrc ? (
            <img src={bannerSrc} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="grid h-full place-items-center text-muted text-xs">
              <span className="flex flex-col items-center gap-2 px-4 text-center">
                <ImagePlus className="h-6 w-6 text-accent" strokeWidth={1.25} />
                No banner yet
              </span>
            </div>
          )}
          {(phase === "processing" || phase === "uploading") && (
            <div className="absolute inset-0 grid place-items-center bg-canvas/70 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2 text-sm text-ink">
                <Loader2 className="h-5 w-5 animate-spin text-accent" strokeWidth={1.75} />
                {phase === "processing" ? "Optimizing…" : "Uploading…"}
              </div>
            </div>
          )}
        </div>
        <div className="p-6">
          <p className="eyebrow text-accent">Banner & theme</p>
          <p className="mt-2 text-sm text-muted">
            Upload a JPEG, PNG, or WebP. The browser resizes and re-encodes into responsive
            variants, samples the dominant colors, and patches them onto the event. For HEIC photos
            from iPhones, convert to JPEG first or share via Mail/Photos which auto-transcodes.
          </p>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              e.currentTarget.value = "";
            }}
          />

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={phase === "processing" || phase === "uploading"}
              className={cn(
                "inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-medium text-canvas",
                "dark:bg-accent dark:text-accent-on shadow-glow hover:bg-ink/90 dark:hover:bg-accent/90 focus-ring",
                "disabled:opacity-60",
              )}
            >
              <ImagePlus className="h-4 w-4" strokeWidth={1.5} />
              {hero.banner ? "Replace banner" : "Upload banner"}
            </button>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-line/70 px-1 py-1">
              <ModePill
                active={mode === "joyful"}
                onClick={() => setMode("joyful")}
                icon={<Sun className="h-3.5 w-3.5" strokeWidth={1.75} />}
                label="Joyful"
              />
              <ModePill
                active={mode === "mourning"}
                onClick={() => setMode("mourning")}
                icon={<Moon className="h-3.5 w-3.5" strokeWidth={1.75} />}
                label="Mourning"
              />
            </div>
          </div>

          {error ? <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p> : null}

          {(suggested || event.theme?.primary) && (
            <ThemeSwatches theme={event.theme} suggestion={suggested} onPick={setSwatch} />
          )}
        </div>
      </div>
    </section>
  );
}

function ThemeSwatches({
  theme,
  suggestion,
  onPick,
}: {
  theme?: EventTheme;
  suggestion?: ExtractedTheme | null;
  onPick: (hex: string, role: "primary" | "accent") => void;
}) {
  const swatches = (suggestion?.swatches ?? []) as string[];
  const primary = (theme?.primary ?? "#B7791F").toUpperCase();
  const accent = (theme?.accent ?? "#E8B14A").toUpperCase();

  return (
    <div className="mt-5 space-y-5">
      <div>
        <p className="eyebrow mb-2.5">Theme colors</p>
        <div className="flex flex-wrap items-center gap-3">
          <ColorField label="Primary" value={primary} onChange={(v) => onPick(v, "primary")} />
          <ColorField label="Accent" value={accent} onChange={(v) => onPick(v, "accent")} />
        </div>
        <p className="mt-2 text-xs text-subtle">Pick any hex value, or tap a suggestion below.</p>
      </div>

      {swatches.length > 0 ? (
        <div>
          <p className="eyebrow mb-2.5">Suggested from the banner</p>
          <div className="flex flex-wrap items-center gap-2">
            {swatches.map((hex) => (
              <SwatchTile key={hex} hex={hex} primary={primary} accent={accent} onPick={onPick} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className="inline-flex items-center gap-2 rounded-full border border-line/70 bg-surface py-1.5 pl-1.5 pr-3">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        aria-label={`${label} color`}
        className="h-7 w-7 cursor-pointer rounded-full border border-line/70 bg-transparent p-0"
      />
      <span className="text-[11px] uppercase tracking-[0.14em] text-muted">{label}</span>
      <span className="font-mono text-xs text-subtle">{value}</span>
    </label>
  );
}

function SwatchTile({
  hex,
  primary,
  accent,
  onPick,
}: {
  hex: string;
  primary: string;
  accent: string;
  onPick: (hex: string, role: "primary" | "accent") => void;
}) {
  const isPrimary = hex.toUpperCase() === primary;
  const isAccent = hex.toUpperCase() === accent;
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-line/70 bg-surface py-1 pl-1 pr-1">
      <span
        className="block h-7 w-7 rounded-full border border-line/40"
        style={{ background: hex }}
        title={hex}
        aria-hidden="true"
      />
      <div className="inline-flex items-center rounded-full bg-paper">
        <button
          type="button"
          onClick={() => onPick(hex, "primary")}
          aria-label={`Set ${hex} as primary color`}
          aria-pressed={isPrimary}
          className={cn(
            "rounded-l-full px-2 py-1 text-[10px] uppercase tracking-wider transition-colors",
            isPrimary
              ? "bg-ink text-canvas dark:bg-accent dark:text-accent-on"
              : "text-muted hover:text-ink",
          )}
        >
          P
        </button>
        <span aria-hidden="true" className="h-3 w-px bg-line/70" />
        <button
          type="button"
          onClick={() => onPick(hex, "accent")}
          aria-label={`Set ${hex} as accent color`}
          aria-pressed={isAccent}
          className={cn(
            "rounded-r-full px-2 py-1 text-[10px] uppercase tracking-wider transition-colors",
            isAccent
              ? "bg-ink text-canvas dark:bg-accent dark:text-accent-on"
              : "text-muted hover:text-ink",
          )}
        >
          A
        </button>
      </div>
    </div>
  );
}

function ModePill({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs uppercase tracking-[0.14em] transition-colors",
        active ? "bg-accent-soft text-accent-ink dark:text-accent" : "text-muted hover:text-ink",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function IconButton({
  onClick,
  label,
  tone,
  ariaLabel,
  disabled,
}: {
  onClick: () => void;
  /** Visual glyph (e.g. "↑", "↓", "✕"). */
  label: string;
  tone?: "danger";
  /** Screen-reader name. Defaults to the glyph, which most readers
   * mispronounce; new callers should always provide this. */
  ariaLabel?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      disabled={disabled}
      className={cn(
        "grid h-8 w-8 place-items-center rounded-full border text-sm transition-colors",
        tone === "danger"
          ? "border-red-300/60 text-red-600 hover:bg-red-50 dark:border-red-400/40 dark:text-red-400 dark:hover:bg-red-950/40"
          : "border-line/70 text-muted hover:border-accent hover:text-accent",
        disabled && "opacity-30 cursor-not-allowed pointer-events-none",
      )}
    >
      {label}
    </button>
  );
}

function LocationEditor({
  event,
  onChange,
}: {
  event: Event;
  onChange: (partial: { modules: Event["modules"] }) => void;
}) {
  const maybeCfg = event.modules.location;
  if (!maybeCfg) return null;
  const cfg = maybeCfg;

  function setCfg(patch: Partial<NonNullable<Event["modules"]["location"]>>) {
    onChange({ modules: { ...event.modules, location: { ...cfg, ...patch } } });
  }

  function useMobeenDefault() {
    // Preserve the admin's custom title if they've set one; only fill
    // it with the default when empty.
    const patch: Partial<NonNullable<Event["modules"]["location"]>> = {
      address: "Mobeen Center\n9625 Black Mountain Rd\nSan Diego, CA 92126",
      lat: 32.89775935422561,
      lng: -117.12279309723516,
      zoom: 15,
    };
    if (!cfg.title?.trim()) {
      patch.title = "Find us at the Mobeen Center";
    }
    setCfg(patch);
  }

  function applyGoogleMapsUrl(raw: string) {
    // Look for @lat,lng in a typical Google Maps share URL.
    const match = raw.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
    if (!match) return;
    setCfg({ lat: Number(match[1]), lng: Number(match[2]) });
  }

  return (
    <section className="surface-card p-6 mb-8">
      <h2 className="font-display text-xl font-light text-ink">Location</h2>
      <p className="mt-1 text-sm text-muted">
        Rendered as an OpenStreetMap embed (no API key) with a "Get directions" CTA.
      </p>

      <div className="mt-6 grid gap-5">
        <label className="block">
          <span className="block text-sm font-medium text-ink">Section title</span>
          <input
            type="text"
            value={cfg.title ?? ""}
            placeholder="Find us at the Mobeen Center"
            onChange={(e) => setCfg({ title: e.target.value })}
            className="form-input"
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-ink">Address</span>
          <textarea
            rows={3}
            value={cfg.address}
            onChange={(e) => setCfg({ address: e.target.value })}
            className="form-input"
            placeholder="Mobeen Center&#10;9625 Black Mountain Rd&#10;San Diego, CA 92126"
          />
          <span className="mt-1.5 block text-xs text-subtle">
            Free-form, multi-line. Shown above the map.
          </span>
        </label>

        <div className="grid gap-5 sm:grid-cols-3">
          <label className="block">
            <span className="block text-sm font-medium text-ink">Latitude</span>
            <input
              type="number"
              step="0.000001"
              value={cfg.lat}
              onChange={(e) => setCfg({ lat: Number(e.target.value) })}
              className="form-input font-mono"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-ink">Longitude</span>
            <input
              type="number"
              step="0.000001"
              value={cfg.lng}
              onChange={(e) => setCfg({ lng: Number(e.target.value) })}
              className="form-input font-mono"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-ink">Zoom</span>
            <input
              type="number"
              min={1}
              max={19}
              step="1"
              value={cfg.zoom ?? 14}
              onChange={(e) => setCfg({ zoom: Number(e.target.value) })}
              className="form-input font-mono"
            />
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={useMobeenDefault}
            className="rounded-full border border-line/70 px-3.5 py-1.5 text-xs uppercase tracking-[0.14em] text-muted hover:border-accent hover:text-accent"
          >
            Use Mobeen Center default
          </button>
          <label className="flex flex-1 min-w-[240px] items-center gap-2 rounded-full border border-line/70 bg-surface px-3 py-1.5 text-xs">
            <span className="shrink-0 text-subtle">Paste Google Maps URL:</span>
            <input
              type="text"
              placeholder="https://maps.app.goo.gl/…"
              onChange={(e) => applyGoogleMapsUrl(e.target.value)}
              className="flex-1 bg-transparent text-ink outline-none placeholder:text-subtle"
            />
          </label>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-muted">
          <input
            type="checkbox"
            checked={cfg.showDirections !== false}
            onChange={(e) => setCfg({ showDirections: e.target.checked })}
            className="h-4 w-4 accent-accent"
          />
          Show "Get directions" CTA on the public page
        </label>
      </div>
    </section>
  );
}
